import type { TableProps } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomTable } from "sipli-fleet-core/components";
import CustomSharedTitleSection from "sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";
import { useQueryGetAuditLogs } from "src/queries/auditLog.queries";
import { useQueryGetCustomersList } from "src/queries/customers.queries";
import { useQueryGetVehicleList } from "src/queries/vehicles.queries";
import type { AuditLogCategory, AuditLogRecord } from "src/services/auditLogService";
import type { Customer } from "src/services/customerService";
import type { VehicleListResponse } from "src/services/vehicleService";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import "./AuditLogData.css";
import AuditLogFilters, { type AuditVehicleOption } from "./AuditLogFilters";

type SourceRecord = Record<string, unknown>;

const DEFAULT_PAGINATION = { page: 0, size: 10 };
const DEFAULT_CATEGORY: AuditLogCategory = "DEVICE";

interface AppliedAuditFilters {
  category: AuditLogCategory;
  vehicle_id?: string | number;
}

type FilterAction = "search" | "reset";

const getAuditLogCategoryKey = (value?: string | null) =>
  value ? `audit_log_category_${value.trim().toLowerCase()}` : "";

const getOptionalDistributorId = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const asRecord = (value: unknown): SourceRecord =>
  value && typeof value === "object" ? (value as SourceRecord) : {};

const readValue = (record: SourceRecord, keys: string[]) => {
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== "") return String(value);
  }
  return "";
};

const pickVehicleItems = (response?: VehicleListResponse | unknown[]): SourceRecord[] => {
  if (Array.isArray(response)) return response.map(asRecord);

  const sources = [
    response?.vehicleInfos,
    response?.vehicles,
    response?.items,
    response?.data,
    response?.content,
  ];
  const source = sources.find((value) => Array.isArray(value));
  return Array.isArray(source) ? source.map(asRecord) : [];
};

const toVehicleOption = (
  item: SourceRecord,
  index: number,
  fallbackLabel: string
): AuditVehicleOption => {
  const device = asRecord(item.device);
  const unit = asRecord(device.unit ?? item.unit);
  const vehicle = asRecord(item.vehicle);
  const vehicleDetails = asRecord(item.vehicleDetails);
  const vehicleId =
    readValue(item, ["vehicleId", "id"]) ||
    readValue(vehicleDetails, ["id"]) ||
    readValue(vehicle, ["id"]);
  const unitId =
    readValue(item, ["unitId"]) || readValue(unit, ["id"]) || readValue(device, ["unitId", "id"]);
  const vehicleName = readValue(item, ["vehicleName", "name"]);
  const imei = readValue(item, ["imei"]) || readValue(unit, ["imei"]);
  const licensePlate = readValue(item, ["licensePlate", "licencePlate", "plate"]);
  const serial = readValue(item, ["serial"]) || readValue(unit, ["serial"]);
  const vin = readValue(item, ["vin", "VIN"]);

  return {
    vehicleId: vehicleId || `vehicle-${index}`,
    unitId,
    label: licensePlate || imei || serial || vin || fallbackLabel,
    vehicleName,
    imei,
    licensePlate,
    serial,
    vin,
  };
};

const getCustomers = (response: unknown): Customer[] => {
  const data = asRecord(response);
  const candidates = [data.customers, data.clients, data.items, data.data];
  const customers = candidates.find((value) => Array.isArray(value));
  return Array.isArray(customers) ? customers.map((item) => asRecord(item) as Customer) : [];
};

const AuditLogDataPage = () => {
  const { t } = useTranslation();
  const selectedDistributor = useSelectDistributor((state) => state.selectedDistributor);
  const distributorId = getOptionalDistributorId(selectedDistributor);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [customerId, setCustomerId] = useState<string>();
  const [vehicleId, setVehicleId] = useState<string>();
  const [category, setCategory] = useState<AuditLogCategory>(DEFAULT_CATEGORY);
  const [appliedFilters, setAppliedFilters] = useState<AppliedAuditFilters>({
    category: DEFAULT_CATEGORY,
    vehicle_id: undefined,
  });
  const [activeFilterAction, setActiveFilterAction] = useState<FilterAction>();

  useEffect(() => {
    setCustomerId(undefined);
    setVehicleId(undefined);
  }, [selectedDistributor]);

  const customersQuery = useQueryGetCustomersList({
    status: "ACTIVE",
    sort: "DESC",
    size: 15,
    page: 0,
    ...(distributorId ? { distributorId } : {}),
  });

  const vehiclesQuery = useQueryGetVehicleList(
    customerId ? { clientId: customerId, size: 15, page: 0, sort: "DESC", all: true } : undefined,
    { enabled: !!customerId }
  );

  const vehicleOptions = useMemo(
    () =>
      pickVehicleItems(vehiclesQuery.data?.data?.response).map((item, index) =>
        toVehicleOption(item, index, t("unnamed_vehicle"))
      ),
    [t, vehiclesQuery.data?.data?.response]
  );
  const selectedVehicle = vehicleOptions.find((vehicle) => vehicle.vehicleId === vehicleId);

  // const deviceDetailsQuery = useQueryDeviceDetails(
  //   selectedVehicle?.unitId,
  //   !!selectedVehicle?.unitId
  // );
  // Previous conditional targetId:
  // const targetId =
  //   category === "UNIT"
  //     ? selectedVehicle?.unitId
  //     : category === "DEVICE"
  //       ? deviceDetailsQuery.data?.device?.id
  //       : category === "RELAY"
  //         ? selectedVehicle?.vehicleId
  //         : null;
  const selectedAuditVehicleId = selectedVehicle?.vehicleId;

  const auditParams = useMemo(
    () => ({
      page: pagination.page,
      size: pagination.size,
      // sort: "DESC",
      // sortBy: "TIMESTAMP",
      category: appliedFilters.category,
      vehicle_id: appliedFilters.vehicle_id,
    }),
    [appliedFilters.category, appliedFilters.vehicle_id, pagination.page, pagination.size]
  );
  const auditQuery = useQueryGetAuditLogs(auditParams);
  const { refetch: refetchAuditLogs } = auditQuery;
  const isAuditLoading = auditQuery.isLoading || auditQuery.isFetching;
  const records = auditQuery.data?.data.events ?? [];
  const totalCount = auditQuery.data?.data.pagination.total_elements ?? 0;

  const customerOptions = useMemo(
    () =>
      getCustomers(customersQuery.data?.data?.response).map((customer) => ({
        value: String(customer.id),
        label: String(customer.companyName ?? customer.name ?? customer.id),
      })),
    [customersQuery.data?.data?.response]
  );

  const applySearchFilters = useCallback(() => {
    const filtersUnchanged =
      appliedFilters.category === category &&
      appliedFilters.vehicle_id === selectedAuditVehicleId;
    const isFirstPage = pagination.page === 0;

    setAppliedFilters({ category, vehicle_id: selectedAuditVehicleId });
    if (!isFirstPage) {
      setPagination((previous) => ({ ...previous, page: 0 }));
    } else if (filtersUnchanged) {
      void refetchAuditLogs();
    }
  }, [
    appliedFilters.category,
    appliedFilters.vehicle_id,
    category,
    pagination.page,
    refetchAuditLogs,
    selectedAuditVehicleId,
  ]);

  const handleSearch = () => {
    setActiveFilterAction("search");
    applySearchFilters();
  };

  const handleResetFilters = () => {
    const appliedFiltersUnchanged =
      appliedFilters.category === DEFAULT_CATEGORY && appliedFilters.vehicle_id === undefined;
    const isFirstPage = pagination.page === 0;

    setActiveFilterAction("reset");
    setCustomerId(undefined);
    setVehicleId(undefined);
    setCategory(DEFAULT_CATEGORY);
    setAppliedFilters({ category: DEFAULT_CATEGORY, vehicle_id: undefined });

    if (!isFirstPage) {
      setPagination((previous) => ({ ...previous, page: 0 }));
    } else if (appliedFiltersUnchanged) {
      void refetchAuditLogs();
    }
  };

  useEffect(() => {
    if (!isAuditLoading) {
      setActiveFilterAction(undefined);
    }
  }, [isAuditLoading]);

  const hasResettableFilters =
    customerId !== undefined ||
    vehicleId !== undefined ||
    category !== DEFAULT_CATEGORY ||
    appliedFilters.category !== DEFAULT_CATEGORY ||
    appliedFilters.vehicle_id !== undefined ||
    pagination.page !== DEFAULT_PAGINATION.page;

  const columns: TableProps<AuditLogRecord>["columns"] = useMemo(
    () => [
      {
        title: t("deviceId"),
        dataIndex: "vehicle_id",
        key: "vehicle_id",
        width: 100,
        render: (value, record) => value ?? record.target_id ?? "--",
      },
      {
        title: t("username"),
        dataIndex: "username",
        key: "username",
        width: 150,
        render: (value) => value ?? "--",
      },
      {
        title: t("category"),
        dataIndex: "category",
        key: "category",
        width: 100,
        render: (value?: string | null) =>
          value ? t(getAuditLogCategoryKey(value), { defaultValue: value }) : "--",
      },
      {
        title: t("operation"),
        dataIndex: "operation",
        key: "operation",
        width: 220,
        render: (value?: string | null) =>
          value ? t(value.trim(), { defaultValue: value }) : "--",
      },
      {
        title: t("description"),
        dataIndex: "description",
        key: "description",
        width: 360,
        render: (value?: string) =>
          value ? <div dangerouslySetInnerHTML={{ __html: value }} /> : "--",
      },
      {
        title: t("timestamp"),
        dataIndex: "created_at",
        key: "created_at",
        width: 140,
        render: (value) => {
          const date = dayjs(value);
          if (!value || !date.isValid()) return "--";

          return (
            <span className="flex flex-col leading-[18px]">
              <span className="font-[600]">{date.format("YYYY-MM-DD")}</span>
              <span className="font-[400]">{date.format("HH:mm:ss")}</span>
            </span>
          );
        },
      },
    ],
    [t]
  );

  return (
    <section className="min-h-full bg-gray1 pb-8 px-2">
      <CustomSharedTitleSection
        titleKey="audit_log"
        showBtn={false}
        classNameContainer="!pt-7 !pb-8"
        titleClassName="!text-[25.23px] !font-[700] !leading-[28px]"
      />

      <AuditLogFilters
        customerId={customerId}
        vehicleId={vehicleId}
        category={category}
        customers={customerOptions}
        vehicles={vehicleOptions}
        isSearchLoading={activeFilterAction === "search" && isAuditLoading}
        isResetLoading={activeFilterAction === "reset" && isAuditLoading}
        isResetDisabled={!hasResettableFilters || isAuditLoading}
        isVehiclesLoading={vehiclesQuery.isLoading || vehiclesQuery.isFetching}
        onCustomerChange={(value) => {
          setCustomerId(value);
          setVehicleId(undefined);
        }}
        onVehicleChange={setVehicleId}
        onCategoryChange={setCategory}
        onSubmit={handleSearch}
        onReset={handleResetFilters}
      />

      <div className="mt-8 overflow-hidden rounded-[16px] bg-white">
        <CustomTable<AuditLogRecord>
          data={records}
          columns={columns}
          customClass="audit-log-table general-audit-log-table"
          rowKey={(record) => record.event_id}
          isLoading={auditQuery.isLoading || auditQuery.isFetching}
          pageSize={pagination.size}
          currentPage={pagination.page}
          totalCount={totalCount}
          handlePagination={(page) => setPagination((previous) => ({ ...previous, page }))}
          handlePageSizeChange={(size) => setPagination({ page: 0, size: Number(size) })}
          showSearchInput={false}
          showCustomizeColumns={false}
          showDownload={false}
        />
      </div>
    </section>
  );
};

export default AuditLogDataPage;
